// src/components/SeasonSelector.tsx
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { queryKeys } from '@/lib/queryClient';
import { queryClient } from '@/lib/queryClient';

import type { Season } from '@/types';

const LS_KEY = 'arma4you.season';
const LS_ALT = 'arma4you-season';

function readLSSeason(): { id: string; label: string } | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;

    const p = JSON.parse(raw);
    // formati supportati:
    // - "2024" (stringa/numero puro)
    // - { id: "2024" }
    // - { id: "2024", label: "2024-2025" }
    if (typeof p === 'string' || typeof p === 'number') {
      return { id: String(p), label: '' }; // label la completiamo dopo
    }
    if (p && p.id) {
      return { id: String(p.id), label: typeof p.label === 'string' ? p.label : '' };
    }
  } catch {}
  return null;
}


function writeLSSeason(s: { id: string; label: string }) {
  // chiave “ufficiale” letta da apiClient/interceptor
  localStorage.setItem(LS_KEY, JSON.stringify(s));

  // (opzionale ma consigliato) allinea anche l’altra chiave se presente,
  // così eventuali store persistiti non ti rimandano alla season "sbagliata" al reload
  try {
    const rawAlt = localStorage.getItem(LS_ALT);
    if (rawAlt) {
      const parsed = JSON.parse(rawAlt);
      // struttura attesa: { state: { seasonId, seasonLabel, seasons: [...] }, version: 0 }
      if (parsed?.state) {
        parsed.state.seasonId = s.id;
        parsed.state.seasonLabel = s.label;
        localStorage.setItem(LS_ALT, JSON.stringify(parsed));
      }
    }
  } catch {}
}


export const useSeasonAwareQuery = () => {

  // 1) carico solo la lista stagioni
  const { data: seasons, isSuccess } = useQuery({
    queryKey: queryKeys.seasons.all,
    queryFn: async () => {
      const res = await apiClient.getSeasons();
      if (res.error) throw new Error(res.error.message);
      return res.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // 2) stagione selezionata
  const [selected, setSelected] = React.useState<{ id: string; label: string } | null>(() => readLSSeason());
// Rimani in sync se cambia la season altrove
React.useEffect(() => {
  const onSeasonChanged = (e: Event) => {
    const detail = (e as CustomEvent)?.detail;
    if (detail?.id) setSelected(prev => (prev?.id === detail.id && prev?.label === detail.label) ? prev : detail);
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === 'arma4you.season') {
      try {
        const v = e.newValue ? JSON.parse(e.newValue) : null;
        if (v?.id) setSelected(v);
      } catch {}
    }
  };
  window.addEventListener('season-changed', onSeasonChanged as EventListener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('season-changed', onSeasonChanged as EventListener);
    window.removeEventListener('storage', onStorage);
  };
}, []);

// 3) inizializzazione + riallineamento label: preferisci LS, poi "current", poi prima stagione
React.useEffect(() => {
  if (!isSuccess || !Array.isArray(seasons) || seasons.length === 0) return;

  const fromLS = readLSSeason();
  if (fromLS && fromLS.id) {
    // Se manca la label in LS, ricavala dalla lista
    const found = seasons.find((s) => s.id === fromLS.id);
    const picked = found ? { id: found.id, label: found.label } : fromLS;
    setSelected(picked);
    writeLSSeason(picked);
    return;
  }

  // Nessuna season in LS -> prendi la current dal DB (o la prima)
  const current = seasons.find((s) => s.is_current);
  const def = current ?? seasons[0];
  const picked = { id: def.id, label: def.label };
  setSelected(picked);
  writeLSSeason(picked);
}, [isSuccess, seasons]);


  // 4) helper per aggiungere il season_id ai filtri
  const withSeasonFilter = React.useCallback(
    (params: Record<string, any>) => {
      if (!selected?.id) return params;
      return { ...params, season_id: selected.id };
    },
    [selected?.id]
  );

  return {
    seasonId: selected?.id ?? null,
    seasonReady: isSuccess && !!selected?.id,
    seasons: seasons ?? [],
    setSeason: (s: Season) => {
      const v = { id: s.id, label: s.label };
      setSelected(v);
      writeLSSeason(v);

        // 1) Notifica globale (tutti i componenti possono ascoltarla)
  window.dispatchEvent(new CustomEvent('season-changed', { detail: v }));
  // 2) Refetch immediato di tutte le query (così non devi cambiare pagina)
  queryClient.invalidateQueries({ predicate: () => true });
    },
    withSeasonFilter,
  };
};

export const SeasonSelector: React.FC = () => {
  const { seasons, seasonId, setSeason } = useSeasonAwareQuery();
  const detailsRef = React.useRef<HTMLDetailsElement>(null);

  return (
    <div className="header-selector relative">
      <details  ref={detailsRef} className="group">
        <summary className="selector-trigger flex items-center cursor-pointer select-none">
          <span className="text-sm text-gray-700 mr-2">
            {seasons.find((s) => s.id === seasonId)?.label ?? 'Seleziona stagione'}
          </span>
          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
        </summary>
        <div className="selector-menu absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-md z-50">
          <ul className="py-1 max-h-80 overflow-y-auto">
            {(seasons ?? []).map((s) => (
              <li key={s.id}>
                <button
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    seasonId === s.id ? 'text-primary-600 font-medium' : 'text-gray-700'
                  }`}
                  onClick={() => {
  setSeason(s);
  // Notifica globale (facoltativa, utile se vuoi ascoltare l'evento altrove)
  window.dispatchEvent(new CustomEvent('season-changed', { detail: { id: s.id } }));
  // Refetch immediato di TUTTE le query (così non devi cambiare pagina)
  queryClient.invalidateQueries({ predicate: () => true });
  detailsRef.current?.removeAttribute('open'); // chiude la dropdown
}}

                >
                  {s.label} {s.is_current ? '•' : ''}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </details>
    </div>
  );
};
